import React, { useState, useEffect, useMemo, useRef, useContext } from "react";
import axios from "axios";
import toast from 'react-hot-toast';
import CustomDropdown from "../CustomDropdownWithPlus";
import CustomTextField from "../CustomTextField";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  ListSubheader,
} from "@mui/material";

import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Stack,
} from "@mui/material";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";

import ButtonLoader from "../ButtonLoader";
import LoadingComponent from "../ComponentLoader";
import AddIcon from "@mui/icons-material/Add";
import { fetchSuppliersList } from "../../services/suppliers";
import { fetchProductionDepartments } from "../../services/productionChargeRouting";
import { fetchAddressList } from "../../services/address";

import {
  getDecoratorsDetails,
  getSingleDecoratorDetails,
  updateDecoratorDetails,
  getDecoratorReceivingAddressOptions,
  updateDecoratorReceivingAddress,
  getChargeMaterialRules,
  saveChargeMaterialRules,
  getProductionMaterialsLite,
  deleteDecoratorCharge,
  setDecoratorChargeArchived,
  getDecoratorMatrixExport,
} from "../.../../../services/decorator";
import styles from "./index.module.css";
import AddDecoratorForm from "./AddDecoratorForm";
import {
  initialDecoratorState,
  decoratiosState,
  formatDecorations,
  createDecoratorPayload,
} from "./index.controller.js";
import DecoratorMatrixForm from "./DecoratorMatrixForm";
import DecoratorRulesSettingsDialog from "./DecoratorRulesSettingsDialog";
import { AuthContext } from "../../helpers/AuthContext";
import {
  receivingAddressSelection,
  groupReceivingAddressOptions,
  canManageDecoratorReceivingAddress,
} from './receivingAddressOptions';

const RULE_TYPE_OPTIONS = [
  { id: "DIRECT_INPUT_PER_PIECE", name: "DIRECT_INPUT_PER_PIECE" },
  { id: "PER_PIECE", name: "PER_PIECE" },
  { id: "FIXED_PER_ORDER", name: "FIXED_PER_ORDER" },
];
const DEFAULT_CURRENCY_SETTINGS = {
  defaultCurrencyCode: "CAD",
  enabledCurrencyCodes: ["CAD"],
  exchangeRates: {},
};
const tableCellInputSx = {
  width: "100%",

  "& .MuiFormControl-root": {
    width: "100%",
  },

  /* Force same height for dropdown and textfield */
  "& .MuiOutlinedInput-root": {
    height: "34px !important",
    borderRadius: "6px",
    fontSize: "13px",
    background: "#fff",

    "& input": {
      padding: "6px 10px !important",
    },

    "& .MuiSelect-select": {
      padding: "6px 10px !important",
      display: "flex",
      alignItems: "center",
    },

    "& fieldset": {
      border: "1px solid #e2e8f0",
    },

    "&:hover fieldset": {
      borderColor: "#cbd5e1",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#2563eb",
    },
  },

  /* Remove spacing created by label wrappers */
  "& .MuiTypography-root": {
    display: "none",
  },

  "& .MuiBox-root": {
    width: "100%",
  },
};

const normalizeCurrencyCode = (value) => String(value || "").trim().toUpperCase();
const createDecorationClientKey = () =>
  `decoration-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const withDecorationClientKey = (decoration = {}) => ({
  ...decoration,
  clientKey: decoration.clientKey || createDecorationClientKey(),
});

const normalizeCurrencySettings = (settings = {}) => {
  const defaultCurrencyCode = normalizeCurrencyCode(
    settings?.defaultCurrencyCode || DEFAULT_CURRENCY_SETTINGS.defaultCurrencyCode
  ) || DEFAULT_CURRENCY_SETTINGS.defaultCurrencyCode;
  const enabledCurrencyCodes = Array.isArray(settings?.enabledCurrencyCodes)
    ? settings.enabledCurrencyCodes.map(normalizeCurrencyCode).filter(Boolean)
    : DEFAULT_CURRENCY_SETTINGS.enabledCurrencyCodes;

  return {
    defaultCurrencyCode,
    enabledCurrencyCodes: Array.from(new Set([defaultCurrencyCode, ...enabledCurrencyCodes])),
    exchangeRates: settings?.exchangeRates || {},
  };
};

const resolveInstanceUniqueId = () => {
  if (typeof window === "undefined") return null;
  const match = window.location.pathname.match(/\/instance\/([^/]+)/i);
  return match?.[1] || null;
};

const DECORATOR_CHARGE_DELETE_ROLES = new Set([
  "administrator",
  "finance-rep",
  "purchasing-rep",
]);

const normalizeRole = (role) =>
  String(role || "").trim().toLowerCase().replace(/[_\s]+/g, "-");

const canDeleteDecoratorCharges = (user = {}) => {
  const roles = Array.isArray(user?.roles)
    ? user.roles
    : user?.roles
      ? [user.roles]
      : [];

  return [...roles, user?.role]
    .map((role) =>
      role && typeof role === "object"
        ? normalizeRole(role.value ?? role.name ?? role.role)
        : normalizeRole(role)
    )
    .filter(Boolean)
    .some((role) => DECORATOR_CHARGE_DELETE_ROLES.has(role));
};

const CHARGES_PAGE_SIZE = 10;

const DecoratorMatrix = ({ open, onClose, instanceId = null }) => {
  const { authState } = useContext(AuthContext);
  const currentUser = authState?.user || authState || {};
  const canDeleteCharge = canDeleteDecoratorCharges(currentUser);
  const [formData, setFormData] = useState(initialDecoratorState);
  const [editingDecorationKey, setEditingDecorationKey] = useState(null);
  const [currentChargesPage, setCurrentChargesPage] = useState(1);
  const [chargeSearchTerm, setChargeSearchTerm] = useState("");
  const [chargeStatus, setChargeStatus] = useState("active");
  const [archivingChargeId, setArchivingChargeId] = useState(null);
  const [decoratorsOptions, setDecoratorsOptions] = useState([]);
  const [openAddDecoratorPopup, setOpenAddDecoratorPopup] = useState(false);
  const [loadingDecorators, setLoadingDecorators] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [productionDepartments, setProductionDepartments] = useState([]);
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    fetchProductionDepartments()
      .then(rows => { if (active) setProductionDepartments((rows || []).filter(d => d.is_active !== false && d.is_deleted !== true && String(d.status || 'active').toLowerCase() === 'active')); })
      .catch(() => { if (active) setProductionDepartments([]); });
    return () => { active = false; };
  }, [open]);
  const [addressOptions, setAddressOptions] = useState([]);
  const [receivingChoices, setReceivingChoices] = useState({ companyAddresses: [], standardAddresses: [] });
  const [linkedCompanyAddressIds, setLinkedCompanyAddressIds] = useState({});
  const [receivingOptionsLoading, setReceivingOptionsLoading] = useState(false);
  const [receivingOptionsError, setReceivingOptionsError] = useState('');
  const [receivingDraft, setReceivingDraft] = useState('');
  const [receivingEditing, setReceivingEditing] = useState(false);
  const [receivingSaving, setReceivingSaving] = useState(false);
  const [receivingSaveError, setReceivingSaveError] = useState('');
  const [currencySettings, setCurrencySettings] = useState(DEFAULT_CURRENCY_SETTINGS);
  const [currencySettingsLoaded, setCurrencySettingsLoaded] = useState(false);
  const [rulesDialogOpen, setRulesDialogOpen] = useState(false);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesSaving, setRulesSaving] = useState(false);
  const [rulesRows, setRulesRows] = useState([]);
  const [rulesMaterials, setRulesMaterials] = useState([]);
  const [rulesCharge, setRulesCharge] = useState(null);
  const [openAuditSettings, setOpenAuditSettings] = useState(false);
  const [deleteChargeTarget, setDeleteChargeTarget] = useState(null);
  const [isDeletingCharge, setIsDeletingCharge] = useState(false);
  const [isExportingMatrix, setIsExportingMatrix] = useState(false);
  const chargeListHeaderRef = useRef(null);
  const previousChargesPageRef = useRef(1);
  const decoratorReadRef = useRef(0);
  const companyDefaultCurrencyCode = currencySettings?.defaultCurrencyCode || "CAD";
  const isEditingCharge = Boolean(editingDecorationKey);
  const canEditReceivingAddress = canManageDecoratorReceivingAddress(currentUser);
  const receivingGroups = useMemo(() => groupReceivingAddressOptions(receivingChoices), [receivingChoices]);
  const currentReceivingSelection = receivingAddressSelection({
    addressId: formData.addressId,
    companyAddressId: linkedCompanyAddressIds[String(formData.selectedDecorator)] || null,
  });
  const displayedReceivingSelection = receivingEditing ? receivingDraft : currentReceivingSelection || 'none';
  const receivingOptionExists = displayedReceivingSelection === 'none' ||
    receivingGroups.all.some((option) => option.id === displayedReceivingSelection);
  const normalizedChargeSearch = String(chargeSearchTerm || "").trim().toLowerCase();
  const filteredDecorations = useMemo(() => {
    const allDecorations = Array.isArray(formData.decorations) ? formData.decorations : [];
    if (!normalizedChargeSearch) return allDecorations;
    return allDecorations.filter((item) =>
      String(item?.chargeName || "").trim().toLowerCase().includes(normalizedChargeSearch)
    );
  }, [formData.decorations, normalizedChargeSearch]);
  const totalCharges = filteredDecorations.length;
  const totalChargesPages = Math.max(1, Math.ceil(totalCharges / CHARGES_PAGE_SIZE));
  const visibleChargesStartIndex = totalCharges === 0 ? 0 : (currentChargesPage - 1) * CHARGES_PAGE_SIZE;
  const visibleChargesEndIndex = Math.min(visibleChargesStartIndex + CHARGES_PAGE_SIZE, totalCharges);
  const paginatedDecorations = filteredDecorations.slice(
    visibleChargesStartIndex,
    visibleChargesEndIndex
  );
  const currencyOptions = (currencySettings?.enabledCurrencyCodes || [companyDefaultCurrencyCode]).map((code) => ({
    id: code,
    name: code,
  }));

  const getPageForDecorationIndex = (index) => Math.floor(index / CHARGES_PAGE_SIZE) + 1;
  const getEditingChargePage = () => {
    if (!editingDecorationKey) return null;
    const editingIndex = (formData.decorations || []).findIndex(
      (item) => item?.clientKey === editingDecorationKey
    );
    if (editingIndex < 0) return null;
    return getPageForDecorationIndex(editingIndex);
  };

  const handleAddDecoration = () => {
    const newDecoration = withDecorationClientKey(
      JSON.parse(JSON.stringify(decoratiosState))
    );
    setChargeSearchTerm("");
    setFormData((prev) => {
      const nextDecorations = [...prev.decorations, newDecoration];
      setCurrentChargesPage(getPageForDecorationIndex(nextDecorations.length - 1));
      return {
        ...prev,
        decorations: nextDecorations,
      };
    });
    setEditingDecorationKey(newDecoration.clientKey);
  };

  const handleUpdateDecoration = (index, updatedData) => {
    const updatedDecorations = [...formData.decorations];
    updatedDecorations[index] = {
      ...updatedDecorations[index],
      ...updatedData,
    };
    setFormData((prev) => ({
      ...prev,
      decorations: updatedDecorations,
    }));
  };

  const selectDecorator = (value, { openAuditSettings = false } = {}) => {
    setReceivingEditing(false);
    setReceivingDraft('');
    setReceivingSaveError('');
    setFormData((prev) => ({
      ...prev,
      selectedDecorator: value,
      addressId: '',
      supplierId: '',
      decorations: [],
      isEliteDecorator: false,
      CurrencyCode: "",
    }));
    setEditingDecorationKey(null);
    setCurrentChargesPage(1);
    setChargeSearchTerm("");
    setChargeStatus("active");
    setOpenAuditSettings(Boolean(openAuditSettings));
  };

  const handleDecoratorChange = (event) => {
    const { value } = event.target;
    selectDecorator(value);
  };

  const handleEditClick = (decorationKey) => {
    if (receivingEditing || receivingSaving) {
      if (typeof window !== "undefined") window.alert("Save or cancel the receiving address first.");
      return;
    }
    if (isEditingCharge && editingDecorationKey !== decorationKey) {
      if (typeof window !== "undefined") {
        window.alert("Save or cancel the current charge before editing another one.");
      }
      return;
    }

    setEditingDecorationKey(decorationKey);
  };

  const handleOpenAuditSettings = () => {
    if (!formData.selectedDecorator) {
      if (typeof window !== "undefined") {
        window.alert("Select a decorator before opening Audit Rules.");
      }
      return;
    }
    if (isEditingCharge || receivingEditing || receivingSaving) {
      if (typeof window !== "undefined") {
        window.alert("Save or cancel the current address or charge before opening Audit Rules.");
      }
      return;
    }
    setOpenAuditSettings(true);
  };

  const handleChangeChargesPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalChargesPages || nextPage === currentChargesPage) return;
    const editingPage = getEditingChargePage();
    if (editingDecorationKey && editingPage && editingPage !== nextPage) {
      if (typeof window !== "undefined") {
        window.alert("Save or cancel the current charge before changing pages.");
      }
      return;
    }
    setCurrentChargesPage(nextPage);
  };

  const handleChargeStatusChange = (nextStatus) => {
    if (isEditingCharge || nextStatus === chargeStatus) return;
    setChargeStatus(nextStatus);
    setChargeSearchTerm("");
    setCurrentChargesPage(1);
  };

  const handleChargeSearchChange = (event) => {
    const nextValue = event?.target?.value ?? "";
    if (isEditingCharge && nextValue !== chargeSearchTerm) {
      if (typeof window !== "undefined") {
        window.alert("Save or cancel the current charge before changing the charge search.");
      }
      return;
    }
    setChargeSearchTerm(nextValue);
    setCurrentChargesPage(1);
  };

  const fetchCurrencySettings = async () => {
    setCurrencySettingsLoaded(false);
    const instanceUniqueId = resolveInstanceUniqueId();
    if (!instanceUniqueId) {
      setCurrencySettings(DEFAULT_CURRENCY_SETTINGS);
      setCurrencySettingsLoaded(true);
      return;
    }

    try {
      const instanceRes = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/byinstanceUniqueId/${instanceUniqueId}`
      );
      const instanceId = instanceRes?.data?.id;
      if (!instanceId) {
        setCurrencySettings(DEFAULT_CURRENCY_SETTINGS);
        setCurrencySettingsLoaded(true);
        return;
      }

      const token = localStorage.getItem("accessToken");
      const defaultsRes = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${instanceId}/defaults`,
        { headers: { accessToken: token } }
      );
      const defaults = defaultsRes?.data?.defaults || {};
      setCurrencySettings(normalizeCurrencySettings(defaults.currencySettings));
      setCurrencySettingsLoaded(true);
    } catch (error) {
      setCurrencySettings(DEFAULT_CURRENCY_SETTINGS);
      setCurrencySettingsLoaded(true);
    }
  };

  const serializeExportValue = (value) => {
    if (value === null || value === undefined) return "";
    if (value instanceof Date) return value.toISOString();
    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch (_error) {
        return String(value);
      }
    }
    return value;
  };

  const prefixExportRow = (prefix, row = {}) =>
    Object.fromEntries(
      Object.entries(row).map(([key, value]) => [
        `${prefix} ${key}`,
        serializeExportValue(value),
      ])
    );

  const makeExportSheet = (XLSX, rows = []) => {
    const safeRows = rows.length ? rows : [{ "No data": "" }];
    const sheet = XLSX.utils.json_to_sheet(safeRows);
    const headers = Object.keys(safeRows[0] || {});
    sheet["!cols"] = headers.map((header) => {
      const longestValue = safeRows.reduce(
        (max, row) => Math.max(max, String(row?.[header] ?? "").length),
        header.length
      );
      return { wch: Math.min(Math.max(longestValue + 2, 12), 48) };
    });
    if (headers.length && rows.length) {
      const lastColumn = XLSX.utils.encode_col(headers.length - 1);
      sheet["!autofilter"] = { ref: `A1:${lastColumn}${rows.length + 1}` };
    }
    return sheet;
  };

  const hasExportData = (value) => {
    if (value === null || value === undefined || value === "") return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object") return Object.keys(value).length > 0;
    return true;
  };

  const getPricingSummary = (charge = {}) => {
    const list = Array.isArray(charge?.ChargesList) ? charge.ChargesList : [];
    if (list.length) {
      const preview = list.slice(0, 4).map((item) => {
        const name = item?.ChargeName || item?.name || "Charge";
        const price = item?.Price ?? item?.price ?? "";
        return `${name}: ${price}`;
      });
      return `${preview.join(" | ")}${list.length > 4 ? ` | +${list.length - 4} more` : ""}`;
    }

    const table = charge?.priceTableData;
    if (table && typeof table === "object") {
      const headers = Array.isArray(table.headers) ? table.headers : [];
      const rows = Array.isArray(table.rows) ? table.rows : [];
      if (headers.length || rows.length) {
        return `Table: ${rows.length} row${rows.length === 1 ? "" : "s"} × ${headers.length} column${headers.length === 1 ? "" : "s"}`;
      }
    }

    return "";
  };

  const handleExportMatrix = async () => {
    if (isExportingMatrix) return;

    setIsExportingMatrix(true);
    try {
      const exportData = await getDecoratorMatrixExport(resolveInstanceUniqueId());
      const decorators = Array.isArray(exportData?.decorators) ? exportData.decorators : [];
      const charges = Array.isArray(exportData?.charges) ? exportData.charges : [];
      const rangeRows = Array.isArray(exportData?.rangeTableEntries)
        ? exportData.rangeTableEntries
        : [];
      const chargePrices = Array.isArray(exportData?.chargePrices)
        ? exportData.chargePrices
        : [];
      const materialRules = Array.isArray(exportData?.materialRules)
        ? exportData.materialRules
        : [];
      const productionRouting = Array.isArray(exportData?.productionRouting)
        ? exportData.productionRouting
        : [];

      const decoratorsById = new Map(
        decorators.map((row) => [String(row?.id ?? ""), row])
      );
      const chargesByDecorator = charges.reduce((map, charge) => {
        const key = String(charge?.DecoratorTitleId ?? "");
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(charge);
        return map;
      }, new Map());
      const materialRulesByCharge = materialRules.reduce((map, row) => {
        const key = String(row?.decoration_charge_id ?? "");
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(row);
        return map;
      }, new Map());
      const routingByCharge = productionRouting.reduce((map, row) => {
        const key = String(row?.chargeId ?? "");
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(row);
        return map;
      }, new Map());

      const duplicateChargeKeys = new Set();
      chargesByDecorator.forEach((decoratorCharges, decoratorId) => {
        const counts = new Map();
        decoratorCharges.forEach((charge) => {
          const normalizedName = String(charge?.ChargeName || "").trim().toLowerCase();
          if (!normalizedName) return;
          counts.set(normalizedName, (counts.get(normalizedName) || 0) + 1);
        });
        counts.forEach((count, normalizedName) => {
          if (count > 1) duplicateChargeKeys.add(`${decoratorId}:${normalizedName}`);
        });
      });

      const reviewRows = charges.map((charge) => {
        const decorator = decoratorsById.get(String(charge?.DecoratorTitleId ?? "")) || {};
        const chargeId = String(charge?.id ?? "");
        const chargeName = String(charge?.ChargeName || "").trim();
        const duplicateKey = `${String(charge?.DecoratorTitleId ?? "")}:${chargeName.toLowerCase()}`;
        const chargeMaterialRules = materialRulesByCharge.get(chargeId) || [];
        const chargeRouting = routingByCharge.get(chargeId) || [];
        const activeRules = chargeMaterialRules.filter(
          (row) => row?.is_active !== false && Number(row?.is_active) !== 0
        );
        const activeRouting = chargeRouting.filter(
          (row) => row?.isActive !== false && Number(row?.isActive) !== 0
        );

        const flags = [];
        if (!chargeName) flags.push("Missing charge name");
        if (chargeName && duplicateChargeKeys.has(duplicateKey)) {
          flags.push("Duplicate charge name");
        }
        if (!hasExportData(charge?.priceTableData) && !hasExportData(charge?.ChargesList)) {
          flags.push("No pricing configured");
        }

        return {
          "Review Flag": flags.join("; "),
          "Action": "",
          "Review Notes": "",
          "Decorator": decorator?.title || "",
          "Charge Name": chargeName,
          "Charge Type": charge?.ChargeType || "",
          "Pricing Type": charge?.CalculationType || "",
          "Requires Proof": charge?.requiresProof === false ? "No" : "Yes",
          "Currency": decorator?.CurrencyCode || "",
          "Description": charge?.Description || "",
          "Rows": charge?.Rows ?? "",
          "Breakdown": charge?.BreakDown ?? "",
          "Units": charge?.Units || "",
          "Pricing Summary": getPricingSummary(charge),
          "Material Rules": activeRules.length,
          "Production Routing": activeRouting.length,
          "Decorator ID": decorator?.id ?? charge?.DecoratorTitleId ?? "",
          "Charge ID": charge?.id ?? "",
          "Supplier ID": charge?.supplierId ?? decorator?.supplierId ?? "",
          "Address ID": charge?.addressId ?? decorator?.addressId ?? "",
          "Created At": charge?.createdAt || "",
          "Updated At": charge?.updatedAt || "",
        };
      });

      const matrixRows = [];
      decorators.forEach((decorator) => {
        const decoratorCharges = chargesByDecorator.get(String(decorator?.id ?? "")) || [];
        if (!decoratorCharges.length) {
          matrixRows.push({
            ...prefixExportRow("Decorator", decorator),
            "Charge id": "",
            "Charge ChargeName": "",
          });
          return;
        }

        decoratorCharges.forEach((charge) => {
          matrixRows.push({
            ...prefixExportRow("Decorator", decorator),
            ...prefixExportRow("Charge", charge),
          });
        });
      });

      const missingNameCount = reviewRows.filter((row) =>
        String(row["Review Flag"] || "").includes("Missing charge name")
      ).length;
      const duplicateNameCount = reviewRows.filter((row) =>
        String(row["Review Flag"] || "").includes("Duplicate charge name")
      ).length;
      const noPricingCount = reviewRows.filter((row) =>
        String(row["Review Flag"] || "").includes("No pricing configured")
      ).length;

      const readmeRows = [
        {
          Section: "Purpose",
          Details:
            "Use Matrix Review to review the current decorator-charge list. Action and Review Notes are intentionally blank working columns. Raw sheets preserve the database export for reference.",
        },
        {
          Section: "Safe workflow",
          Details:
            "Review and annotate this workbook before making system changes. Do not change Decorator ID or Charge ID; those are stable record identifiers.",
        },
        {
          Section: "Review Flag",
          Details:
            "Flags are informational only. They identify objective conditions such as missing charge names, duplicate names under the same decorator, or missing pricing configuration.",
        },
        {
          Section: "Action",
          Details:
            "Decorator Matrix Import treats this as an instruction column. Use UPDATE or NO CHANGE to keep a charge active (and restore the same Charge ID if it is archived), NEW for a blank-ID new charge, and DELETE / RETIRE / ARCHIVE to archive an existing charge. RESTORE / UNARCHIVE / REACTIVATE are also accepted explicit restore actions.",
        },
      ];

      const summaryRows = [
        { Metric: "Exported at", Value: exportData?.generatedAt || new Date().toISOString() },
        { Metric: "Instance ID", Value: exportData?.instanceId ?? "" },
        { Metric: "Decorators", Value: decorators.length },
        { Metric: "Charges", Value: charges.length },
        { Metric: "Charges missing a name", Value: missingNameCount },
        { Metric: "Rows with duplicate charge names", Value: duplicateNameCount },
        { Metric: "Charges with no pricing configured", Value: noPricingCount },
        { Metric: "Range table entries", Value: rangeRows.length },
        { Metric: "Charge prices", Value: chargePrices.length },
        { Metric: "Material rules", Value: materialRules.length },
        { Metric: "Production routing rows", Value: productionRouting.length },
      ];

      const XLSX = await import("xlsx");
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        makeExportSheet(XLSX, readmeRows),
        "README"
      );
      XLSX.utils.book_append_sheet(
        workbook,
        makeExportSheet(XLSX, reviewRows),
        "Matrix Review"
      );
      XLSX.utils.book_append_sheet(
        workbook,
        makeExportSheet(XLSX, summaryRows),
        "Summary"
      );
      XLSX.utils.book_append_sheet(
        workbook,
        makeExportSheet(XLSX, matrixRows),
        "Decorator Charges"
      );
      XLSX.utils.book_append_sheet(
        workbook,
        makeExportSheet(
          XLSX,
          decorators.map((row) => prefixExportRow("Decorator", row))
        ),
        "Decorators Raw"
      );
      XLSX.utils.book_append_sheet(
        workbook,
        makeExportSheet(
          XLSX,
          charges.map((row) => prefixExportRow("Charge", row))
        ),
        "Charges Raw"
      );
      XLSX.utils.book_append_sheet(
        workbook,
        makeExportSheet(
          XLSX,
          rangeRows.map((row) => prefixExportRow("Range", row))
        ),
        "Range Table"
      );
      XLSX.utils.book_append_sheet(
        workbook,
        makeExportSheet(
          XLSX,
          chargePrices.map((row) => prefixExportRow("Price", row))
        ),
        "Charge Prices"
      );
      XLSX.utils.book_append_sheet(
        workbook,
        makeExportSheet(
          XLSX,
          materialRules.map((row) => prefixExportRow("Material Rule", row))
        ),
        "Material Rules"
      );
      XLSX.utils.book_append_sheet(
        workbook,
        makeExportSheet(
          XLSX,
          productionRouting.map((row) => prefixExportRow("Routing", row))
        ),
        "Production Routing"
      );

      workbook.Props = {
        Title: "Elite Core Decorator Matrix Export",
        Subject: "Decorator and charge review export",
        Author: "Elite Core",
        Comments: "Generated from Company Settings > Decorator Matrix",
        CreatedDate: new Date(),
      };

      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `decorator-matrix-${stamp}.xlsx`);
    } catch (error) {
      console.error("Failed to export decorator matrix:", error);
      if (typeof window !== "undefined") {
        window.alert(
          error?.response?.data?.message ||
          "Unable to export the decorator matrix. Please try again."
        );
      }
    } finally {
      setIsExportingMatrix(false);
    }
  };

  const loadReceivingChoices = async () => {
    const result = await getDecoratorReceivingAddressOptions(resolveInstanceUniqueId());
    setReceivingChoices({
      companyAddresses: result?.companyAddresses || [],
      standardAddresses: result?.standardAddresses || [],
    });
    setLinkedCompanyAddressIds(result?.linkedCompanyAddressIds || {});
    setReceivingOptionsError('');
    return result;
  };

  useEffect(() => {
    if (!open) return;
    let active = true;
    setReceivingOptionsLoading(true);
    setReceivingOptionsError('');
    getDecoratorReceivingAddressOptions(resolveInstanceUniqueId())
      .then((result) => {
        if (!active) return;
        setReceivingChoices({
          companyAddresses: result?.companyAddresses || [],
          standardAddresses: result?.standardAddresses || [],
        });
        setLinkedCompanyAddressIds(result?.linkedCompanyAddressIds || {});
      })
      .catch((error) => {
        if (!active) return;
        setReceivingOptionsError(error?.response?.data?.message || 'Unable to load receiving addresses.');
      })
      .finally(() => { if (active) setReceivingOptionsLoading(false); });
    return () => { active = false; };
  }, [open]);

  const handleReceivingAddressSelection = (event) => {
    const value = event.target.value;
    setReceivingSaveError('');
    setReceivingDraft(value);
    setReceivingEditing(value !== (currentReceivingSelection || 'none'));
  };

  const saveReceivingAddress = async () => {
    if (!formData.selectedDecorator || !receivingEditing || !receivingDraft || receivingSaving) return;
    setReceivingSaving(true);
    setReceivingSaveError('');
    try {
      const decoratorId = formData.selectedDecorator;
      const saved = await updateDecoratorReceivingAddress(decoratorId, receivingDraft, resolveInstanceUniqueId());
      // Update only the address, not the 88+ charges or their unsaved editor state.
      setFormData((previous) => String(previous.selectedDecorator) === String(decoratorId)
        ? { ...previous, addressId: saved.addressId } : previous);
      setLinkedCompanyAddressIds((previous) => ({
        ...previous, [String(decoratorId)]: saved.companyAddressId,
      }));
      setReceivingEditing(false);
      setReceivingDraft('');
      toast.success('Decorator receiving address saved.');
      try {
        await loadReceivingChoices();
      } catch (error) {
        setReceivingOptionsError('Address saved, but options could not be refreshed. Please retry.');
      }
    } catch (error) {
      setReceivingSaveError(error?.response?.data?.message || error?.message ||
        'Unable to save the decorator receiving address.');
    } finally {
      setReceivingSaving(false);
    }
  };

  const fetchDecorators = async () => {
    try {
      const response = await getDecoratorsDetails();
      const suppliers = await fetchSuppliersList();
      let addressResponse = await fetchAddressList();
      addressResponse = addressResponse.map((address) => ({
        id: address.id,
        name:
          address.addressName || `${address.streetAddress} - ${address.city}`,
      }));
      setAddressOptions(addressResponse || []);
      setSupplierOptions(suppliers);

      const options = response?.result.map((decorator) => ({
        name: decorator.title,
        id: decorator.id.toString(),
      }));
      setDecoratorsOptions(options);
    } catch (error) {}
  };
  const handleCopyDecoration = (index, data) => {
    const { id, clientKey, ...rest } = data;
    const copiedDecoration = withDecorationClientKey(rest);
    setFormData((prev) => {
      const updated = [...prev.decorations];
      updated.splice(index + 1, 0, copiedDecoration);
      setCurrentChargesPage(getPageForDecorationIndex(index + 1));
      return {
        ...prev,
        decorations: updated,
      };
    });
    setEditingDecorationKey(copiedDecoration.clientKey);
  };
  const removeChargeFromLocalState = (index) => {
    const updatedDecorations = formData.decorations.filter(
      (_item, itemIndex) => itemIndex !== index
    );

    setFormData((prev) => ({
      ...prev,
      decorations: updatedDecorations,
    }));
    setEditingDecorationKey(null);

    if (updatedDecorations.length === 0) {
      setCurrentChargesPage(1);
    } else {
      const nextIndex = Math.min(index, updatedDecorations.length - 1);
      setCurrentChargesPage(getPageForDecorationIndex(nextIndex));
    }
  };

  const handleDeleteDecoration = (index, decorationInfo) => {
    if (decorationInfo?.id) {
      if (!canDeleteCharge) return;
      setDeleteChargeTarget({
        index,
        id: decorationInfo.id,
        name: decorationInfo.chargeName || decorationInfo.ChargeName || "this charge",
      });
      return;
    }

    // Unsaved rows only exist locally and can be removed without hitting the API.
    removeChargeFromLocalState(index);
  };

  const closeDeleteChargeConfirm = () => {
    if (isDeletingCharge) return;
    setDeleteChargeTarget(null);
  };

  const confirmDeleteCharge = async () => {
    if (!deleteChargeTarget?.id || !formData.selectedDecorator || isDeletingCharge) return;

    setIsDeletingCharge(true);
    try {
      await deleteDecoratorCharge(
        formData.selectedDecorator,
        deleteChargeTarget.id,
        resolveInstanceUniqueId()
      );
      removeChargeFromLocalState(deleteChargeTarget.index);
      setDeleteChargeTarget(null);
      await fetchSingleDecorator();
    } catch (error) {
      console.error("Failed to delete decorator charge:", error);
    } finally {
      setIsDeletingCharge(false);
    }
  };

  const handleArchiveToggle = async (decorationInfo, archived) => {
    if (!decorationInfo?.id || !formData.selectedDecorator || archivingChargeId) return;
    setArchivingChargeId(decorationInfo.id);
    try {
      await setDecoratorChargeArchived(
        formData.selectedDecorator,
        decorationInfo.id,
        archived,
        resolveInstanceUniqueId()
      );
      await fetchSingleDecorator();
    } catch (error) {
      console.error("Failed to update decorator charge archive state:", error);
      if (typeof window !== "undefined") {
        window.alert(
          error?.response?.data?.message ||
          `Unable to ${archived ? "archive" : "restore"} this charge.`
        );
      }
    } finally {
      setArchivingChargeId(null);
    }
  };
  useEffect(() => {
    fetchDecorators();
    return () => {
      setFormData(initialDecoratorState);
      setEditingDecorationKey(null);
    };
  }, []);
  useEffect(() => {
    if (!open) {
      // Reset state when dialog is closed
      setFormData(initialDecoratorState);
      setEditingDecorationKey(null);
      setReceivingEditing(false);
      setReceivingDraft('');
      setReceivingSaveError('');
      setOpenAddDecoratorPopup(false);
      setOpenAuditSettings(false);
      setCurrencySettings(DEFAULT_CURRENCY_SETTINGS);
      setCurrencySettingsLoaded(false);
      setCurrentChargesPage(1);
      setChargeSearchTerm("");
      setChargeStatus("active");
      setArchivingChargeId(null);
      return;
    }
    fetchCurrencySettings();
  }, [open]);

  const fetchSingleDecorator = async () => {
    const requestId = ++decoratorReadRef.current;
    const selectedDecoratorId = formData.selectedDecorator;
    setLoadingDecorators(true);
    try {
      const response = await getSingleDecoratorDetails(
        selectedDecoratorId,
        resolveInstanceUniqueId(),
        null,
        chargeStatus
      );
      if (requestId !== decoratorReadRef.current) return;
      if (!response) throw new Error('Decorator details could not be loaded.');
      const decorations = formatDecorations(response.Decorations);
      setFormData((previous) => String(previous.selectedDecorator) === String(selectedDecoratorId)
        ? {
          ...previous,
          supplierId: response.supplierId,
          addressId: response.addressId,
          CurrencyCode: normalizeCurrencyCode(
            response.CurrencyCode || response.currencyCode || companyDefaultCurrencyCode || 'CAD'
          ),
          decorations: decorations.map(withDecorationClientKey),
          isEliteDecorator: response.isEliteDecorator || false,
        } : previous
      );
      setEditingDecorationKey(null);
      setCurrentChargesPage(1);
    } catch (error) {
      if (requestId === decoratorReadRef.current) {
        console.error('Failed to fetch decorator details:', error);
      }
    } finally {
      if (requestId === decoratorReadRef.current) setLoadingDecorators(false);
    }
  };
  useEffect(() => {
    if (!formData.selectedDecorator || !currencySettingsLoaded) return;
    fetchSingleDecorator();
  }, [formData.selectedDecorator, currencySettingsLoaded, chargeStatus]);

  const handleSelect = (e, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: e?.target?.value,
    }));
  };

  const saveDecoratorMatrix = async (nextFormData = formData) => {
    if (nextFormData.isEliteDecorator && nextFormData.decorations.some((dec) => !dec.id && !Number(dec.productionDepartmentId))) {
      toast.error('Select a production department for each new internal Elite charge.');
      return;
    }
    try {
      setIsSaving(true);
      const payload = createDecoratorPayload(nextFormData, companyDefaultCurrencyCode);
      // Charge edits must never overwrite the separately managed receiving address.
      delete payload.addressId;
      await updateDecoratorDetails(
        nextFormData.selectedDecorator,
        payload,
      );
      setIsSaving(false);
      await fetchSingleDecorator();
      setEditingDecorationKey(null);
    } catch (error) {
      setIsSaving(false);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to save decorator charge.');
    }
  };

  const onSave = async () => {
    await saveDecoratorMatrix(formData);
  };

  const handleCancelEdit = async () => {
    await fetchSingleDecorator();
    setEditingDecorationKey(null);
  };

  useEffect(() => {
    if (currentChargesPage > totalChargesPages) {
      setCurrentChargesPage(totalChargesPages);
    }
  }, [currentChargesPage, totalChargesPages]);

  useEffect(() => {
    if (previousChargesPageRef.current !== currentChargesPage) {
      chargeListHeaderRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    previousChargesPageRef.current = currentChargesPage;
  }, [currentChargesPage]);

  const openProductionRules = async (decorationInfo) => {
    if (!decorationInfo?.id) return;
    setRulesCharge(decorationInfo);
    setRulesDialogOpen(true);
    setRulesLoading(true);
    try {
      const [materialsRes, rulesRes] = await Promise.all([
        getProductionMaterialsLite(),
        getChargeMaterialRules(decorationInfo.id),
      ]);
      setRulesMaterials(materialsRes?.data || []);
      setRulesRows(
        (rulesRes?.data || []).map((r) => ({
          id: r.id,
          material_id: r.material_id,
          rule_type: r.rule_type,
          factor_or_qty: r.factor_or_qty ?? "",
        })),
      );
    } catch (e) {
      setRulesMaterials([]);
      setRulesRows([]);
    } finally {
      setRulesLoading(false);
    }
  };

  const addRuleRow = () => {
    setRulesRows((prev) => [
      ...prev,
      {
        id: null,
        material_id: "",
        rule_type: "DIRECT_INPUT_PER_PIECE",
        factor_or_qty: "",
      },
    ]);
  };

  const removeRuleRow = (idx) => {
    setRulesRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateRuleRow = (idx, patch) => {
    setRulesRows((prev) =>
      prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)),
    );
  };

  const onSaveRules = async () => {
    if (!rulesCharge?.id) return;
    setRulesSaving(true);
    try {
      const payload = rulesRows
        .filter((r) => r.material_id && r.rule_type)
        .map((r) => ({
          material_id: Number(r.material_id),
          rule_type: r.rule_type,
          factor_or_qty:
            r.factor_or_qty == null || r.factor_or_qty === ""
              ? null
              : Number(r.factor_or_qty),
        }));
      await saveChargeMaterialRules(rulesCharge.id, payload);
      setRulesDialogOpen(false);
    } finally {
      setRulesSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      // fullW
      // idth
      fullWidth
      PaperProps={{
        sx: {
          minWidth: "90%",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6">Decorator Matrix</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Export every decorator and charge in this instance to Excel">
            <span>
              <Button
                variant="contained"
                startIcon={<DownloadRoundedIcon />}
                onClick={handleExportMatrix}
                disabled={isExportingMatrix}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "9px",
                  px: 2,
                  background: "linear-gradient(135deg, #15803d 0%, #16a34a 100%)",
                  boxShadow: "0 6px 16px rgba(22, 163, 74, 0.22)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #166534 0%, #15803d 100%)",
                    boxShadow: "0 8px 20px rgba(22, 163, 74, 0.28)",
                  },
                  "&.Mui-disabled": {
                    background: "#bbf7d0",
                    color: "#166534",
                  },
                }}
              >
                {isExportingMatrix ? "Exporting..." : "Export Matrix"}
              </Button>
            </span>
          </Tooltip>
          <Button
            className={styles.addDecoratorButton}
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddDecoratorPopup(true)}
          >
            Add Decorator
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: "#e8eeef" }}>
        <Box sx={{ padding: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "end",
              gap: 2,
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", marginLeft: "20px", flexWrap: "wrap", rowGap: 2 }}>
              <Box sx={{ width: "350px" }}>
                <CustomDropdown
                  label="Decorator"
                  name="decorator"
                  options={decoratorsOptions}
                  handleSelect={handleDecoratorChange}
                  selectedValue={formData.selectedDecorator}
                  disabled={isEditingCharge || receivingEditing || receivingSaving}
                />
              </Box>
              {formData.selectedDecorator && !formData.isEliteDecorator && (
                <Box
                  className="field"
                  sx={{ marginLeft: "20px", width: "350px" }}
                >
                  <CustomDropdown
                    name="supplierId"
                    options={supplierOptions}
                    handleSelect={handleSelect}
                    minWidth={"150px"}
                    selectedValue={formData.supplierId}
                    placeholder="Select Supplier"
                    label="Supplier"
                    disabled={!isEditingCharge}
                  />
                </Box>
              )}
              {formData.selectedDecorator && (
                <>
                  <Box sx={{ marginLeft: "20px", width: { xs: "100%", sm: "420px" } }}>
                    <Typography sx={{ fontSize: "0.9rem", color: "#555" }}>Receiving Address</Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        aria-label="Decorator Receiving Address"
                        value={displayedReceivingSelection}
                        onChange={handleReceivingAddressSelection}
                        disabled={!canEditReceivingAddress || loadingDecorators || receivingOptionsLoading || Boolean(receivingOptionsError) || isEditingCharge || receivingSaving}
                        SelectProps={{ displayEmpty: true }}
                        sx={{ backgroundColor: "#fff", "& .MuiInputBase-root": { minHeight: 34 } }}
                      >
                        <MenuItem value="none">No default receiving address (choose in Shipping)</MenuItem>
                        {displayedReceivingSelection && !receivingOptionExists && (
                          <MenuItem value={displayedReceivingSelection} disabled>
                            {displayedReceivingSelection.startsWith('company-')
                              ? 'Previously selected company address is unavailable'
                              : `Previously saved address #${formData.addressId || 'unknown'} (unavailable)`}
                          </MenuItem>
                        )}
                        <ListSubheader>Company Contact Info</ListSubheader>
                        {receivingGroups.companies.map((option) => (
                          <MenuItem key={option.id} value={option.id} disabled={option.disabled}>
                            {option.name}{option.disabled ? ' (incomplete)' : ''}
                          </MenuItem>
                        ))}
                        <ListSubheader>Saved Addresses</ListSubheader>
                        {receivingGroups.saved.map((option) => (
                          <MenuItem key={option.id} value={option.id} disabled={option.disabled}>
                            {option.name}{option.disabled ? ' (incomplete)' : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                      {receivingEditing && (
                        <>
                          <Button size="small" variant="contained" onClick={saveReceivingAddress} disabled={receivingSaving || isEditingCharge}>
                            {receivingSaving ? 'Saving' : 'Save'}
                          </Button>
                          <Button size="small" onClick={() => {
                            setReceivingEditing(false);
                            setReceivingDraft('');
                            setReceivingSaveError('');
                          }} disabled={receivingSaving}>Cancel</Button>
                        </>
                      )}
                    </Box>
                    {!canEditReceivingAddress && !receivingOptionsLoading && (
                      <Typography variant="caption" color="text.secondary">
                        Administrator or Super Administrator access is required to change this receiving address.
                      </Typography>
                    )}
                    {displayedReceivingSelection === 'none' && !receivingOptionsLoading && (
                      <Typography variant="caption" color="text.secondary">
                        No default. Sales reps can choose the receiving address in Shipping; a complete address is required before generating the supplier PO.
                      </Typography>
                    )}
                    {receivingOptionsLoading && <Typography variant="caption">Loading available addresses…</Typography>}
                    {receivingOptionsError && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="caption" color="error">{receivingOptionsError}</Typography>
                        <Button size="small" onClick={async () => {
                          setReceivingOptionsLoading(true);
                          try { await loadReceivingChoices(); }
                          catch (error) { setReceivingOptionsError(error?.response?.data?.message || 'Unable to load receiving addresses.'); }
                          finally { setReceivingOptionsLoading(false); }
                        }}>Retry</Button>
                      </Box>
                    )}
                    {receivingSaveError && <Typography variant="caption" color="error">{receivingSaveError}</Typography>}
                    {!receivingOptionsError && !receivingOptionsLoading && displayedReceivingSelection.startsWith('company-') && (
                      <Typography variant="caption" color="text.secondary">
                        Linked to Company Contact Info for new shipments. Existing POs are unchanged.
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ marginLeft: "20px", width: "180px" }}>
                    <CustomDropdown
                      label={"Currency"}
                      name="CurrencyCode"
                      disabled={!isEditingCharge}
                      handleSelect={handleSelect}
                      selectedValue={formData.CurrencyCode || companyDefaultCurrencyCode}
                      options={currencyOptions}
                    />
                  </Box>
                </>
              )}
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                className={styles.addDecorationButton}
                startIcon={
                  <AddIcon
                    sx={{
                      color: "rgb(25 118 210) !important",
                    }}
                  />
                }
                onClick={handleAddDecoration}
                disabled={!formData.selectedDecorator || isEditingCharge || receivingEditing || receivingSaving || chargeStatus === "archived"}
              >
                Add Charge
              </Button>
              <Button
                variant="outlined"
                onClick={handleOpenAuditSettings}
                disabled={!formData.selectedDecorator || isEditingCharge || receivingEditing || receivingSaving}
              >
                Audit Rules
              </Button>
            </Box>
          </Box>
          {!loadingDecorators ? (
            <>
              {formData.selectedDecorator && (
                <Paper
                  ref={chargeListHeaderRef}
                  variant="outlined"
                  sx={{
                    mb: 2,
                    px: 2,
                    py: 1.5,
                    borderRadius: "10px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ minWidth: { xs: "100%", md: "320px" }, flex: 1 }}>
                    <CustomTextField
                      label="Search Charges"
                      name="chargeSearch"
                      placeholder="Search by charge name"
                      value={chargeSearchTerm}
                      handleChange={handleChargeSearchChange}
                    />
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Chip
                      label="Active"
                      color={chargeStatus === "active" ? "primary" : "default"}
                      variant={chargeStatus === "active" ? "filled" : "outlined"}
                      onClick={() => handleChargeStatusChange("active")}
                      clickable={!isEditingCharge}
                    />
                    <Chip
                      label="Archived"
                      color={chargeStatus === "archived" ? "warning" : "default"}
                      variant={chargeStatus === "archived" ? "filled" : "outlined"}
                      onClick={() => handleChargeStatusChange("archived")}
                      clickable={!isEditingCharge}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {normalizedChargeSearch
                        ? `${totalCharges} matching ${chargeStatus} charge${totalCharges === 1 ? "" : "s"}`
                        : `${filteredDecorations.length} ${chargeStatus} charge${filteredDecorations.length === 1 ? "" : "s"}`}
                    </Typography>
                  </Box>
                </Paper>
              )}
              {paginatedDecorations.map((item, pageIndex) => {
                const index = visibleChargesStartIndex + pageIndex;
                const originalIndex = (formData.decorations || []).findIndex(
                  (decoration) => decoration?.clientKey === item?.clientKey
                );
                return (
              <DecoratorMatrixForm
                isEliteDecorator={Boolean(formData.isEliteDecorator)}
                productionDepartments={productionDepartments}
                key={item.clientKey || (originalIndex >= 0 ? originalIndex : index)}
                isEditMode={editingDecorationKey === item.clientKey}
                isSaving={isSaving}
                index={originalIndex >= 0 ? originalIndex : index}
                decorationInfo={item}
                formData={formData}
                handleUpdateDecoration={handleUpdateDecoration}
                onCopy={handleCopyDecoration}
                onDelete={handleDeleteDecoration}
                canDeleteCharge={canDeleteCharge}
                onArchiveToggle={handleArchiveToggle}
                isArchivingCharge={String(archivingChargeId || "") === String(item?.id || "")}
                onOpenProductionRules={openProductionRules}
                onEdit={handleEditClick}
                onSave={onSave}
                onCancel={handleCancelEdit}
                canStartEdit={!isEditingCharge || editingDecorationKey === item.clientKey}
              />
                );
              })}
              {formData.selectedDecorator && totalCharges > 0 && (
                <Paper
                  variant="outlined"
                  sx={{
                    mt: 2,
                    px: 2,
                    py: 1.5,
                    borderRadius: "10px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {`Showing ${visibleChargesStartIndex + 1}-${visibleChargesEndIndex} of ${totalCharges} charges`}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleChangeChargesPage(currentChargesPage - 1)}
                      disabled={currentChargesPage <= 1}
                    >
                      Previous
                    </Button>
                    <Typography variant="body2" sx={{ minWidth: "72px", textAlign: "center" }}>
                      {`Page ${currentChargesPage} of ${totalChargesPages}`}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleChangeChargesPage(currentChargesPage + 1)}
                      disabled={currentChargesPage >= totalChargesPages}
                    >
                      Next
                    </Button>
                  </Box>
                </Paper>
              )}
              {formData.selectedDecorator && totalCharges === 0 && normalizedChargeSearch && (
                <Paper
                  variant="outlined"
                  sx={{ mt: 2, px: 2, py: 2, borderRadius: "10px", textAlign: "center" }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No charges found.
                  </Typography>
                </Paper>
              )}
              {formData.selectedDecorator && totalCharges === 0 && !normalizedChargeSearch && (
                <Paper
                  variant="outlined"
                  sx={{ mt: 2, px: 2, py: 2, borderRadius: "10px", textAlign: "center" }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {chargeStatus === "archived"
                      ? "No archived charges for this decorator."
                      : "No charges yet. Use Add Charge to create the first matrix row for this decorator."}
                  </Typography>
                </Paper>
              )}
            </>
          ) : (
            <>
              <LoadingComponent />
            </>
          )}
        </Box>
      </DialogContent>

      <Dialog
        open={Boolean(deleteChargeTarget)}
        onClose={closeDeleteChargeConfirm}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete charge?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete <strong>{deleteChargeTarget?.name}</strong>?
            This permanently removes this specific decorator charge and its saved pricing/rules.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteChargeConfirm} disabled={isDeletingCharge}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDeleteCharge}
            disabled={isDeletingCharge}
          >
            {isDeletingCharge ? "Deleting..." : "Delete charge"}
          </Button>
        </DialogActions>
      </Dialog>

      <AddDecoratorForm
        handleDecoratorChange
        open={openAddDecoratorPopup}
        onClose={() => setOpenAddDecoratorPopup(false)}
        fetchDecorators={fetchDecorators}
        supplierOptions={supplierOptions}
        CustomDropdown={CustomDropdown}
        handleSelect={handleSelect}
        addressOptions={addressOptions}
        currencyOptions={currencyOptions}
        defaultCurrencyCode={companyDefaultCurrencyCode}
      />

      <DecoratorRulesSettingsDialog
        open={openAuditSettings}
        onClose={() => setOpenAuditSettings(false)}
        currentInstanceId={instanceId}
        decoratorsOptions={decoratorsOptions}
        decorator={
          formData.selectedDecorator
            ? {
                id: Number(formData.selectedDecorator),
                name:
                  decoratorsOptions.find(
                    (option) =>
                      String(option.id) === String(formData.selectedDecorator)
                  )?.name || "Decorator",
              }
            : null
        }
      />

      <Dialog
        open={rulesDialogOpen}
        onClose={() => setRulesDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "10px",
            overflow: "hidden",
            boxShadow: "0 12px 40px rgba(0,0,0,0.14)",
            minWidth: "820px",
          },
        }}
      >
        {/*  Header */}
        <DialogTitle
          sx={{
            px: 3,
            py: 1.8,
            background: "#ffffff",
            borderBottom: "1px solid #e8edf2",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: "8px",
                background: "#eff6ff",
                border: "1px solid #dbeafe",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <TuneIcon sx={{ color: "#2563eb", fontSize: 17 }} />
            </Box>
            <Box>
              <Typography
                sx={{
                  color: "#0f172a",
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: 1.3,
                }}
              >
                Production Rules
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#94a3b8", fontWeight: 400 }}
              >
                {rulesCharge?.chargeName ||
                  rulesCharge?.ChargeName ||
                  "Charge Configuration"}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            {rulesRows.length > 0 && (
              <Chip
                label={`${rulesRows.length} Rule${
                  rulesRows.length !== 1 ? "s" : ""
                }`}
                size="small"
                sx={{
                  background: "#eff6ff",
                  color: "#2563eb",
                  fontWeight: 600,
                  fontSize: "11px",
                  height: "22px",
                  border: "1px solid #bfdbfe",
                  borderRadius: "6px",
                }}
              />
            )}
            <Tooltip title="Close">
              <IconButton
                size="small"
                onClick={() => setRulesDialogOpen(false)}
                sx={{
                  color: "#94a3b8",
                  borderRadius: "6px",
                  "&:hover": { color: "#334155", background: "#f1f5f9" },
                }}
              >
                <CloseIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </DialogTitle>

        {/* Content  */}
        <DialogContent sx={{ p: 0, background: "#f8fafc" }}>
          {rulesLoading ? (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
              <LoadingComponent />
            </Box>
          ) : (
            <Box>
              {/* Table */}
              <Paper
                elevation={0}
                sx={{
                  mx: 2.5,
                  mt: 2.5,
                  borderRadius: "8px",
                  border: "1px solid #e2e8f0",
                  overflow: "hidden",
                }}
              >
                <Table size="small" sx={{ tableLayout: "fixed" }}>
                  {/* Table Head */}
                  <TableHead>
                    <TableRow sx={{ background: "#f1f5f9" }}>
                      <TableCell
                        sx={{
                          width: "40px",
                          py: 1.2,
                          px: 2,
                          borderBottom: "1px solid #e2e8f0",
                          color: "#94a3b8",
                          fontSize: "10.5px",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.6px",
                        }}
                      >
                        #
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "40%",
                          py: 1.2,
                          px: 1.5,
                          borderBottom: "1px solid #e2e8f0",
                          color: "#94a3b8",
                          fontSize: "10.5px",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.6px",
                        }}
                      >
                        Material
                      </TableCell>
                      <TableCell
                        sx={{
                          width: "30%",

                          py: 1.2,
                          px: 1.5,
                          borderBottom: "1px solid #e2e8f0",
                          color: "#94a3b8",
                          fontSize: "10.5px",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.6px",
                        }}
                      >
                        Rule Type
                      </TableCell>
                      <TableCell
                        sx={{
                          // width: '170px',
                          width: "20%",
                          py: 1.2,
                          px: 1.5,
                          borderBottom: "1px solid #e2e8f0",
                          color: "#94a3b8",
                          fontSize: "10.5px",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.6px",
                        }}
                      >
                        Factor / Qty
                      </TableCell>
                      <TableCell
                        sx={{
                          // width: '80px',
                          width: "90px",
                          py: 1.2,
                          px: 1.5,
                          borderBottom: "1px solid #e2e8f0",
                        }}
                      />
                    </TableRow>
                  </TableHead>

                  {/* Table Body */}
                  <TableBody>
                    {rulesRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ border: "none", py: 0 }}>
                          <Box
                            sx={{
                              py: 6,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 46,
                                height: 46,
                                borderRadius: "10px",
                                background: "#f1f5f9",
                                border: "1px solid #e2e8f0",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mb: 0.5,
                              }}
                            >
                              <TuneIcon
                                sx={{ fontSize: 22, color: "#cbd5e1" }}
                              />
                            </Box>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{ color: "#475569", fontSize: "13px" }}
                            >
                              No rules configured yet
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "#94a3b8" }}
                            >
                              Click "Add Rule" below to get started
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rulesRows.map((r, idx) => (
                        <TableRow
                          key={`${r.id || "new"}-${idx}`}
                          sx={{
                            background: "#ffffff",
                            transition: "all 0.15s ease",

                            "&:hover": {
                              background: "#f8fafc",
                            },

                            "&:last-child td": {
                              borderBottom: "none",
                            },
                          }}
                        >
                          {/* Row # */}
                          <TableCell
                            sx={{
                              px: 2,
                              py: 1,
                              borderBottom: "1px solid #f1f5f9",
                              verticalAlign: "middle",
                            }}
                          >
                            <Typography
                              variant="caption"
                              fontWeight={600}
                              sx={{ color: "#cbd5e1", fontSize: "11px" }}
                            >
                              {idx + 1}
                            </Typography>
                          </TableCell>

                          {/* Material Cell */}
                          <TableCell
                            sx={{
                              px: 1.5,
                              py: 0.6,
                              borderBottom: "1px solid #f1f5f9",
                              verticalAlign: "middle",
                            }}
                          >
                            <Box
                              sx={{
                                ...tableCellInputSx,
                                "& .MuiSelect-root": {
                                  width: "100% !important",
                                  minWidth: "0 !important",
                                },
                              }}
                            >
                              <CustomDropdown
                                name={`material-${idx}`}
                                options={(rulesMaterials || []).map((m) => ({
                                  id: String(m.id),
                                  name: `${m.name}${
                                    m.item_code ? ` (${m.item_code})` : ""
                                  }`,
                                }))}
                                selectedValue={
                                  r.material_id ? String(r.material_id) : ""
                                }
                                handleSelect={(e) =>
                                  updateRuleRow(idx, {
                                    material_id: Number(e?.target?.value),
                                  })
                                }
                                placeholder="Select material"
                              />
                            </Box>
                          </TableCell>

                          {/* Rule Type Cell */}
                          <TableCell
                            sx={{
                              px: 1.5,
                              py: 0.6,
                              borderBottom: "1px solid #f1f5f9",
                              verticalAlign: "middle",
                            }}
                          >
                            <Box sx={tableCellInputSx}>
                              <CustomDropdown
                                name={`rule-${idx}`}
                                options={RULE_TYPE_OPTIONS}
                                selectedValue={
                                  r.rule_type || "DIRECT_INPUT_PER_PIECE"
                                }
                                handleSelect={(e) =>
                                  updateRuleRow(idx, {
                                    rule_type: e?.target?.value,
                                  })
                                }
                              />
                            </Box>
                          </TableCell>

                          {/* Factor / Qty Cell */}
                          <TableCell
                            sx={{
                              px: 1.5,
                              py: 0.6,
                              borderBottom: "1px solid #f1f5f9",
                              verticalAlign: "middle",
                            }}
                          >
                            <Box sx={tableCellInputSx}>
                              <CustomTextField
                                name={`factor-${idx}`}
                                value={r.factor_or_qty}
                                handleChange={(e) =>
                                  updateRuleRow(idx, {
                                    factor_or_qty: e.target.value,
                                  })
                                }
                                placeholder="0.00"
                              />
                            </Box>
                          </TableCell>

                          {/* Actions */}
                          <TableCell
                            sx={{
                              px: 1.5,
                              py: 0.6,
                              borderBottom: "1px solid #f1f5f9",
                              verticalAlign: "middle",
                            }}
                          >
                            <Stack
                              className="row-actions"
                              direction="row"
                              spacing={0.3}
                              justifyContent="flex-end"
                              alignItems="center"
                            >
                              <Tooltip title="Duplicate row" placement="top">
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setRulesRows((prev) => {
                                      const copy = { ...prev[idx], id: null };
                                      const updated = [...prev];
                                      updated.splice(idx + 1, 0, copy);
                                      return updated;
                                    })
                                  }
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "6px",
                                    border: "1px solid #e2e8f0",
                                    background: "#ffffff",
                                    color: "#64748b",

                                    "&:hover": {
                                      background: "#f1f5f9",
                                      color: "#2563eb",
                                      borderColor: "#cbd5e1",
                                    },
                                  }}
                                >
                                  <ContentCopyIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                              </Tooltip>

                              <Tooltip title="Remove row" placement="top">
                                <IconButton
                                  size="small"
                                  onClick={() => removeRuleRow(idx)}
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: "6px",
                                    border: "1px solid #fee2e2",
                                    background: "#ffffff",
                                    color: "#ef4444",

                                    "&:hover": {
                                      background: "#fef2f2",
                                      color: "#dc2626",
                                      borderColor: "#fecaca",
                                    },
                                  }}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>

              {/* Add Rule */}
              <Box sx={{ px: 2.5, py: 2 }}>
                <Button
                  variant="text"
                  startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />}
                  onClick={addRuleRow}
                  size="small"
                  sx={{
                    color: "#2563eb",
                    fontWeight: 500,
                    fontSize: "13px",
                    textTransform: "none",
                    px: 1.5,
                    py: 0.6,
                    borderRadius: "6px",
                    "&:hover": {
                      background: "#eff6ff",
                    },
                  }}
                >
                  Add Rule
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>

        {/* Footer */}
        <DialogActions
          sx={{
            px: 3,
            py: 1.8,
            background: "#ffffff",
            borderTop: "1px solid #e8edf2",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="caption" sx={{ color: "#94a3b8" }}>
            {rulesRows.length > 0
              ? `${rulesRows.length} rule${
                  rulesRows.length !== 1 ? "s" : ""
                } will be saved`
              : "No rules defined"}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button
              onClick={() => setRulesDialogOpen(false)}
              variant="outlined"
              size="small"
              sx={{
                borderRadius: "7px",
                color: "#64748b",
                borderColor: "#e2e8f0",
                px: 2.5,
                fontWeight: 500,
                fontSize: "13px",
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { borderColor: "#94a3b8", background: "#f8fafc" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={onSaveRules}
              disabled={rulesSaving}
              size="small"
              disableElevation
              sx={{
                borderRadius: "7px",
                background: "#2563eb",
                px: 2.5,
                fontWeight: 500,
                fontSize: "13px",
                textTransform: "none",
                "&:hover": { background: "#1d4ed8" },
                "&:disabled": { background: "#bfdbfe", color: "#fff" },
              }}
            >
              {rulesSaving ? <ButtonLoader /> : "Save Rules"}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DecoratorMatrix;
